// Transcription + medical analysis of the in-consultation recorder.
//
// These used to call api.openai.com directly from the browser with the key
// embedded in the build (VITE_OPENAI_API_KEY) — anyone could read it out of
// the shipped JS or the network tab. Both calls now go through our own
// backend (RecorderAiController), which holds the OpenAI key server-side
// only and logs the real token usage itself.
import { recorderAnalyzeApi, recorderTranscribeApi } from "../../services/apiService";

/**
 * Request 1 — Transcription
 * POST {API_BASE_URL}doctor/recorder/transcribe
 */
export async function transcribeChunk(blob, filename = "chunk.webm", appointmentId = null) {
  const formData = new FormData();
  formData.append("file", blob, filename);
  if (appointmentId != null) {
    formData.append("appointment_id", appointmentId);
  }

  const response = await recorderTranscribeApi(formData);
  const data = response.data ?? {};
  const text = (data.fullText ?? "").trim();
  return {
    fullText: text,
    lines: text ? [{ speaker: "Speaker", text }] : [],
    usage: data.usage ?? null,
    model: data.model ?? "gpt-4o-transcribe",
  };
}

export const EMPTY_PROPOSITIONS = {
  observations: [],
};

/**
 * Request 2 — Medical Analysis (LLM)
 * POST {API_BASE_URL}doctor/recorder/analyze
 */
export async function analyzeTranscript(newText, appointmentId = null) {
  if (!newText || !newText.trim()) {
    return { ...EMPTY_PROPOSITIONS, revised_transcript: "" };
  }

  const response = await recorderAnalyzeApi({
    text: newText,
    appointment_id: appointmentId ?? undefined,
  });
  const data = response.data ?? {};

  return {
    ...EMPTY_PROPOSITIONS,
    observations: Array.isArray(data.observations) ? data.observations : [],
    revised_transcript: typeof data.revised_transcript === "string" ? data.revised_transcript : "",
    usage: data.usage ?? null,
    model: data.model ?? "gpt-4o",
  };
}

let idCounter = 0;
function nextId(prefix) {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

function normalizePropositionText(raw) {
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return Object.values(raw)
      .filter((v) => v !== null && v !== undefined && String(v).trim().length > 0)
      .map((v) => String(v).trim())
      .join(" — ");
  }
  return String(raw ?? "").trim();
}

/**
 * Merge a fresh analysis into the running list of suggestions, de-duplicating
 * near-identical entries (case-insensitive). Each item is { id, text, added }.
 */
export function mergeObservations(accumulated, incoming) {
  const existing = Array.isArray(accumulated) ? accumulated : [];
  const additions = Array.isArray(incoming?.observations) ? incoming.observations : [];
  const combined = [...existing];
  for (const raw of additions) {
    const text = normalizePropositionText(raw);
    if (
      text &&
      !combined.some((item) => item.text.trim().toLowerCase() === text.toLowerCase())
    ) {
      combined.push({ id: nextId("obs"), text, added: false });
    }
  }
  return combined;
}

/** Object-shaped merge, mirroring the prototype's mergePropositions. */
export function mergePropositions(accumulated, incoming) {
  return { observations: mergeObservations(accumulated?.observations, incoming) };
}

/**
 * Parses a "Doctor: ...\nPatient: ..." block (revised_transcript) into the
 * { speaker, text } line shape used by the transcript view — ported from the
 * prototype's parseSpeakerBlock.
 */
export function parseSpeakerBlock(text) {
  if (!text || !text.trim()) return [];
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const result = [];
  let lastSpeaker = "Doctor";
  for (const line of lines) {
    const match = line.match(/^(Doctor|Patient)\s*:\s*(.*)$/i);
    if (match) {
      lastSpeaker = /^doctor$/i.test(match[1]) ? "Doctor" : "Patient";
      result.push({ speaker: lastSpeaker, text: match[2] });
    } else {
      result.push({ speaker: lastSpeaker, text: line });
    }
  }
  return result;
}

export function nextManualId() {
  return nextId("manual");
}
